import { TFunction } from 'next-i18next'

import { isGroup } from '@/utils/listings'
import { Listing } from '@/components/listing-card-new/types'

import { BedsBaths, ListingCardProps } from '../ListingCard.types'

/**
 * Represents price information for a listing, aligning with the `price` prop
 * defined across the various interfaces in `ListingCard.types.ts`.
 *
 * The price structure supports:
 *
 * 1.  Single price: `{ min: 1000 }` - displays as "$1,000/month"
 * 2.  Price range: `{ min: 1000, max: 1500 }` - displays as "$1,000–$1,500/month" (for buildings)
 * 3.  Label override: `{ label: "Call for Price" }` - displays as "Call for Price"
 */
export type Price =
  | { min: number; max?: number; label?: never }
  | { min?: never; max?: never; label: string }

export interface FormatOptions {
  /** Currency symbol to prepend (default "$") */
  currency?: string
  /** Text to append (e.g. "/month", default "/month") */
  period?: string
  /**
   * When true and no `max` is provided, appends a "+" after the min price.
   * e.g. "$3,000+" instead of "$3,000"
   */
  plusIfNoMax?: boolean
}

/**
 * Format a Price object into a display string.
 *
 * Examples:
 *   formatPrice({ min: 3000 })                   // "$3,000/month"
 *   formatPrice({ min: 3000 }, { plusIfNoMax: true }) // "$3,000+/month"
 *   formatPrice({ min: 3000, max: 5000 })        // "$3,000–$5,000/month"
 *   formatPrice({ min: 1000, label: "Call for Price" }) // "Call for Price"
 *   formatPrice({ min: 0, label: "Rented" })     // "Rented"
 */
export function formatPrice(
  price: Price,
  { currency = '$', period = '/month', plusIfNoMax = false }: FormatOptions = {}
): { price: string; period: string } {
  // If a label is provided, it takes precedence over everything
  if (price.label) {
    return { price: price.label, period: '' }
  }

  if (typeof price.min !== 'number') {
    throw new Error(
      'Invalid price: min must be a number when label is not provided'
    )
  }

  const formattedMin = `${currency}${price.min.toLocaleString()}`

  // If max is defined, show a price range (for buildings)
  if (typeof price.max === 'number') {
    const formattedMax = `${currency}${price.max.toLocaleString()}`
    return { price: `${formattedMin}–${formattedMax}`, period }
  }

  // Single price: decide whether to append a "+" or not
  const plusSuffix = plusIfNoMax ? '+' : ''
  return { price: `${formattedMin}${plusSuffix}`, period }
}

/**
 * Extracts the minimum price from a single listing
 */
export const getListingMinPrice = (listing: Listing) => {
  if (!listing) return null

  // For groups, use gr_min_price
  if (isGroup(listing) && listing.gr_min_price) {
    const price = Number(listing.gr_min_price)
    return isNaN(price) ? null : price
  }

  // For regular listings, use price
  if (listing.price) {
    const price = Number(listing.price)
    return isNaN(price) ? null : price
  }

  return null
}

export const getListingMaxPrice = (listing: Listing) => {
  if (!listing) return null

  if (isGroup(listing) && listing.gr_max_price) {
    const price = Number(listing.gr_max_price)
    return isNaN(price) ? null : price
  }

  if (listing.price) {
    const price = Number(listing.price)
    return isNaN(price) ? null : price
  }

  return null
}

/**
 * Calculates the minimum price from an array of listings
 */
export const getBuildingPrice = (
  listings: Listing[],
  variant: ListingCardProps['variant'],
  t: TFunction
) => {
  if (!Array.isArray(listings) || listings.length === 0) {
    return { label: 'Error getting price' }
  }

  const minPrices = listings
    .map(getListingMinPrice)
    .filter(
      (price): price is number =>
        price !== null && price !== undefined && price > 0
    )

  const maxPrices = listings
    .map(getListingMaxPrice)
    .filter(
      (price): price is number =>
        price !== null && price !== undefined && price > 0
    )

  const prices = [...minPrices, ...maxPrices]

  if (minPrices.length === 0) {
    return { label: 'Error getting price' }
  }

  switch (variant) {
    case 'variousRates':
      return {
        label: t('Various Rates')
      }
    case 'multiUnit':
      return {
        min: minPrices[0]
      }
    case 'building':
      return {
        min: Math.min(...(prices as number[])),
        max: Math.max(...(prices as number[]))
      }
    case 'locked':
      return {
        label: t('Locked')
      }
    case 'rented':
      return {
        label: t('common.rented', 'Rented')
      }

    default:
      return {
        min: minPrices[0]
      }
  }
}

export const getBuildingBedsBaths = (listings: Listing[]) => {
  if (!Array.isArray(listings) || listings.length === 0) {
    return null
  }

  const beds = listings
    .map(listing => listing.bedrooms)
    .filter(bed => bed !== null && bed !== undefined)
  const baths = listings
    .map(listing => listing.bathrooms)
    .filter(bath => bath !== null && bath !== undefined)

  if (beds.length === 0 || baths.length === 0) {
    return null
  }

  return {
    beds: {
      min: Math.min(...(beds as number[])),
      max: Math.max(...(beds as number[]))
    },
    baths: {
      min: Math.min(...(baths as number[])),
      max: Math.max(...(baths as number[]))
    }
  }
}

const formatBedCount = (count: number, t: TFunction): string => {
  return count === 0
    ? t('common.studio', 'Studio')
    : t('{{bedCount}} Bed', { bedCount: count })
}

const formatBedRange = (min: number, max: number, t: TFunction): string => {
  if (min === 0 && max === 0) {
    return t('common.studio', 'Studio')
  }
  if (min === 0) {
    return `${t('common.studio', 'Studio')} – ${formatBedCount(max, t)}`
  }
  if (min === max) {
    return formatBedCount(min, t)
  }
  return `${formatBedCount(min, t)} – ${formatBedCount(max, t)}`
}

export const formatBedsBaths = (
  bedsBaths: BedsBaths | null | undefined,
  variant: ListingCardProps['variant'],
  t: TFunction
) => {
  if (!bedsBaths || !bedsBaths.beds || !bedsBaths.baths) {
    return t('{{bedCount}} Bed · {{bathCount}} Bath', {
      bedCount: '--',
      bathCount: '--'
    })
  }

  switch (variant) {
    case 'locked':
      return t('{{bedCount}} Bed · {{bathCount}} Bath', {
        bedCount: '*',
        bathCount: '*'
      })
    case 'building':
    case 'variousRates':
      return formatBedRange(bedsBaths.beds.min, bedsBaths.beds.max, t)
    default:
      return `${formatBedCount(bedsBaths.beds.min, t)} · ${
        bedsBaths.baths.min
      } ${t('Bath')}`
  }
}
